import { useCallback, useEffect, useState } from "react";
import { VipPlanField } from "@/components/admin/VipPlanField";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Loader2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/layout/PageHero";
import { ConfirmButton } from "@/components/admin/ConfirmButton";
import { StatusBadge } from "@/components/dev/BillingCard";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const KINDS = ["developer", "minisite", "both"];
const customOf = (c) => (c ? Number(c) || null : null);

const KindSelect = ({ value, onChange, testId, compact }) => {
  const { t } = useI18n();
  const cls = compact ? "rounded-md border border-neutral-700 bg-black/40 px-2 py-1 text-xs" : "w-full rounded-md border border-neutral-700 bg-black/50 px-3 py-2 text-sm";
  return (
    <select value={value} aria-label={t("adminVip.kind")} className={`min-w-0 max-w-full text-white ${cls}`} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {KINDS.map((k) => <option key={k} value={k}>{t(`adminVip.kind.${k}`)}</option>)}
    </select>
  );
};

// Mini-site role: free extra employees (0–50) returned as extra_employees by the access API.
const EmployeesField = ({ value, onChange, testId, compact }) => {
  const { t } = useI18n();
  return (
    <label className={`flex min-w-0 flex-wrap items-center gap-2 text-xs text-neutral-300 ${compact ? "" : "pt-1"}`}>
      <span className="break-words">{t("emp.vipField")}</span>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} className="rounded-md border border-neutral-700 bg-black/40 px-2 py-1 text-xs text-white" data-testid={testId}>
        {Array.from({ length: 51 }, (_, i) => <option key={i} value={i}>{i}</option>)}
      </select>
    </label>
  );
};

const Account = ({ acc, role, vipId }) => {
  const { t } = useI18n();
  const tid = `vip-row-${role}-${vipId}`;
  if (!acc) return <span className="text-xs text-neutral-500" data-testid={`${tid}-none`}>{t(`adminVip.role.${role}`)} · {t("adminVip.noAccount")}</span>;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2" data-testid={tid}>
      <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-neutral-500">{t(`adminVip.role.${role}`)}</span>
      {role === "developer"
        ? <Link to={`/developpeurs/${acc.slug}`} className="break-all text-sm text-amber-200 hover:text-amber-100" data-testid={`${tid}-link`}>{acc.brand}</Link>
        : <span className="break-all text-sm text-emerald-200">{acc.brand}</span>}
      <StatusBadge status={acc.status} testId={`${tid}-status`} />
    </span>
  );
};

const VipRow = ({ v, onRemove, onChanged }) => {
  const { t, fmtDate } = useI18n();
  const save = (body) => api.put(`/admin/vip/${v.id}/kind`, { kind: v.kind, plan: v.plan, custom_apps: v.custom_apps, employees: v.employees || 0, ...body })
    .then(() => { toast.success(t("plans.changed")); onChanged(); }).catch((err) => toast.error(translateError(err, t)));
  const shown = v.kind === "both" ? ["developer", "minisite"] : [v.kind];
  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-2xl border border-[rgba(245,197,66,0.25)] bg-black/30 p-4 sm:flex-row sm:items-center" data-testid={`vip-row-${v.id}`}>
      <div className="min-w-0 flex-1">
        <p className="break-all font-semibold text-white" data-testid="vip-row-email">{v.email}</p>
        {v.note && <p className="mt-0.5 break-words text-xs text-neutral-400" data-testid="vip-row-note">{v.note}</p>}
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
          <KindSelect compact value={v.kind} onChange={(kind) => save({ kind })} testId={`vip-row-kind-${v.id}`} />
          {v.kind !== "developer" && <EmployeesField compact value={v.employees || 0} onChange={(employees) => save({ employees })} testId={`vip-row-employees-${v.id}`} />}
          {v.kind !== "minisite" && <VipPlanField compact value={v.plan || "standard"} customApps={v.custom_apps} onChange={(plan, c) => save({ plan, custom_apps: customOf(c) })} testId={`vip-row-plan-${v.id}`} />}
        </div>
        <p className="mt-1 text-xs text-neutral-500">{fmtDate(v.created_at)}</p>
      </div>
      <div className="flex min-w-0 flex-col gap-2 sm:items-end">
        {shown.map((r) => <Account key={r} acc={v[r]} role={r} vipId={v.id} />)}
        <ConfirmButton title={t("adminVip.removeTitle")} description={t("adminVip.removeDesc", { email: v.email })} onConfirm={() => onRemove(v.id)}
          className="self-end rounded-full p-2 text-red-300 hover:bg-white/5" testId={`vip-remove-btn-${v.id}`}>
          <Trash2 className="h-4 w-4" />
        </ConfirmButton>
      </div>
    </li>
  );
};

export default function AdminVip() {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [kind, setKind] = useState("developer");
  const [employees, setEmployees] = useState(0);
  const [plan, setPlan] = useState({ plan: "standard", custom: null });
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const load = useCallback(() => api.get("/admin/vip").then(({ data }) => setList(data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/vip", { email, note, kind, plan: plan.plan, custom_apps: customOf(plan.custom), employees });
      toast.success(t("adminVip.added"));
      setEmail("");
      setNote("");
      setKind("developer");
      setEmployees(0);
      setPlan({ plan: "standard", custom: null });
      setFormKey((k) => k + 1);
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/vip/${id}`);
      toast.success(t("adminVip.removed"));
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };

  return (
    <div data-testid="admin-vip-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("adminVip.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("adminVip.intro")}</p>
      </PageHero>
      <div className="mx-auto max-w-4xl space-y-6 px-4 sm:px-8">
        <form onSubmit={add} className="card-carbon grid min-w-0 gap-4 rounded-2xl p-6 sm:grid-cols-[1fr_1fr_auto] sm:items-start" data-testid="vip-add-form">
          <div className="min-w-0 space-y-2">
            <Label htmlFor="vip-email">{t("login.email")}</Label>
            <Input id="vip-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-black/50" data-testid="vip-email-input" />
            <Label htmlFor="vip-kind" className="block pt-1">{t("adminVip.kind")}</Label>
            <KindSelect value={kind} onChange={setKind} testId="vip-kind-select" />
            <p className="break-words text-xs text-neutral-500" data-testid="vip-kind-hint">{t(`adminVip.kindHint.${kind}`)}</p>
            {kind !== "developer" && <EmployeesField value={employees} onChange={setEmployees} testId="vip-employees-select" />}
          </div>
          <div className="min-w-0 space-y-2">
            <Label htmlFor="vip-note">{t("adminVip.note")}</Label>
            <Input id="vip-note" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} className="bg-black/50" data-testid="vip-note-input" />
            {kind !== "minisite" && <VipPlanField key={formKey} value={plan.plan} onChange={(p, c) => setPlan({ plan: p, custom: c })} testId="vip-plan-select" />}
          </div>
          <button type="submit" className="btn-gold btn-cta sm:mt-7" disabled={busy} data-testid="vip-add-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("adminVip.add")}
          </button>
        </form>
        <section className="card-carbon rounded-2xl p-6">
          <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
            <Crown className="h-5 w-5 text-[var(--gold)]" /> {t("adminVip.listTitle")} <span className="text-[var(--gold)]" data-testid="vip-count">({list?.length ?? 0})</span>
          </h2>
          {list === null && <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />}
          {list?.length === 0 && <p className="text-sm text-neutral-400" data-testid="vip-empty">{t("adminVip.empty")}</p>}
          <ul className="space-y-3" data-testid="vip-list">{list?.map((v) => <VipRow key={`${v.id}-${v.kind}-${v.plan}-${v.custom_apps}`} v={v} onRemove={remove} onChanged={load} />)}</ul>
        </section>
      </div>
    </div>
  );
}
